(() => {
  // 二重ロード防止: cart_type=drawer 時は /cart で main-cart-items と cart-drawer の
  // 両方が本JSを読み込むため、IIFEが2回走ってリスナー/初期化が重複する（Codex P2）。
  if (window.__dtCartRecoSliderInit) return;
  window.__dtCartRecoSliderInit = true;

  const SELECTOR = 'slider-component[data-cart-reco-slider]';
  const AUTOPLAY_INTERVAL = 4000;
  const MANUAL_RESUME_DELAY = 6000;
  const NORMALIZE_DELAY = 140;
  const NORMALIZE_IDLE_MS = 120;
  const NORMALIZE_RETRY_DELAY = 80;
  const NORMALIZE_MAX_RETRIES = 8;
  const CLONE_ATTR = 'data-cart-reco-clone';
  const instances = new WeakMap();
  const liveInstances = new Set(); // 切り離された旧instanceの掃除用（Ajax再描画のリーク防止・Codex P2）

  class CartRecoSlider {
    constructor(root) {
      this.root = root;
      this.slider = root.querySelector('[id^="Slider-"]');
      this.prevButton = root.querySelector('button[name="previous"]');
      this.nextButton = root.querySelector('button[name="next"]');

      if (!this.slider || !this.prevButton || !this.nextButton) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.hoverCapableQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = null;
      this.resumeTimer = null;
      this.normalizeTimer = null;
      this.resizeObserver = null;
      this.isHovered = false;
      this.isDocumentHidden = document.hidden;
      this.manualPausedUntil = 0;
      this.leadingCloneCount = 0;
      this.currentIndex = 0;
      this.lastScrollEventAt = 0;
      this.normalizeRetryCount = 0;

      this.refresh();
      this.bindEvents();
      this.startAutoplayIfEligible();
    }

    bindEvents() {
      this.prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.goToPrevious();
        this.pauseAfterManualInteraction();
      }, { signal: this.signal });

      this.nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.goToNext();
        this.pauseAfterManualInteraction();
      }, { signal: this.signal });

      this.slider.addEventListener('scroll', () => {
        this.lastScrollEventAt = Date.now();
        this.syncCurrentIndex();
        this.scheduleNormalize();
      }, { passive: true, signal: this.signal });

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.slider);

      if (this.hoverCapableQuery.matches) {
        this.root.addEventListener('mouseenter', () => {
          this.isHovered = true;
          this.stopAutoplay();
        }, { signal: this.signal });

        this.root.addEventListener('mouseleave', () => {
          this.isHovered = false;
          this.startAutoplayIfEligible();
        }, { signal: this.signal });
      }

      document.addEventListener('visibilitychange', () => {
        this.isDocumentHidden = document.hidden;
        if (this.isDocumentHidden) {
          this.stopAutoplay();
          return;
        }
        this.startAutoplayIfEligible();
      }, { signal: this.signal });

      this.reducedMotionQuery.addEventListener('change', () => {
        if (this.reducedMotionQuery.matches) {
          this.stopAutoplay();
          return;
        }
        this.startAutoplayIfEligible();
      }, { signal: this.signal });
    }

    refresh() {
      const previousIndex = this.currentIndex || 0;
      this.removeClones();

      this.slides = this.getOriginalSlides();
      this.slideCount = this.slides.length;
      this.currentIndex = this.clampIndex(previousIndex);

      if (this.slideCount <= 1) {
        this.stopAutoplay();
        this.leadingCloneCount = 0;
        this.renderSlides = this.slides;
        this.renderPositions = this.renderSlides.map((slide) => slide.offsetLeft);
        return;
      }

      this.buildLoopClones();
      this.renderSlides = this.getRenderSlides();
      this.renderPositions = this.renderSlides.map((slide) => slide.offsetLeft);
      this.normalizeToLogicalIndex(this.currentIndex);
      this.syncCurrentIndex();
    }

    getOriginalSlides() {
      return Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter((item) => item.clientWidth > 0);
    }

    getRenderSlides() {
      return Array.from(this.slider.children).filter((item) => item.clientWidth > 0);
    }

    buildLoopClones() {
      if (this.slideCount <= 1) return;

      const firstSlide = this.slides[0];
      const lastSlide = this.slides[this.slideCount - 1];

      const trailingClone = this.createClone(firstSlide, 0);
      const leadingClone = this.createClone(lastSlide, this.slideCount - 1);

      this.slider.prepend(leadingClone);
      this.slider.append(trailingClone);
      this.leadingCloneCount = 1;
    }

    createClone(sourceSlide, sourceIndex) {
      const clone = sourceSlide.cloneNode(true);
      clone.setAttribute(CLONE_ATTR, 'true');
      clone.dataset.cloneOfIndex = String(sourceIndex);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      this.stripIds(clone);
      this.disableFocusables(clone);
      return clone;
    }

    stripIds(node) {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.hasAttribute('id')) node.removeAttribute('id');
      Array.from(node.children).forEach((child) => this.stripIds(child));
    }

    disableFocusables(node) {
      const focusables = node.querySelectorAll('a, button, input, select, textarea, [tabindex]');
      focusables.forEach((element) => {
        element.setAttribute('tabindex', '-1');
        if (element.hasAttribute('id')) {
          element.removeAttribute('id');
        }
      });
    }

    removeClones() {
      this.slider.querySelectorAll(`[${CLONE_ATTR}]`).forEach((clone) => clone.remove());
    }

    clampIndex(index) {
      if (!Number.isFinite(index) || this.slideCount <= 0) return 0;
      return Math.min(Math.max(index, 0), this.slideCount - 1);
    }

    getRenderIndexForLogicalIndex(index) {
      const logicalIndex = this.clampIndex(index);
      return logicalIndex + this.leadingCloneCount;
    }

    getClosestRenderIndex() {
      if (!this.renderSlides || this.renderSlides.length === 0) return 0;
      const left = this.slider.scrollLeft;
      let closestIndex = 0;
      let minDistance = Number.POSITIVE_INFINITY;

      this.renderSlides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    syncCurrentIndex() {
      if (this.slideCount <= 0) {
        this.currentIndex = 0;
        return;
      }

      const renderIndex = this.getClosestRenderIndex();

      if (renderIndex < this.leadingCloneCount) {
        this.currentIndex = this.slideCount - 1;
        return;
      }

      const trailingCloneStart = this.leadingCloneCount + this.slideCount;
      if (renderIndex >= trailingCloneStart) {
        this.currentIndex = 0;
        return;
      }

      this.currentIndex = this.clampIndex(renderIndex - this.leadingCloneCount);
    }

    goToIndex(index, behavior = 'smooth') {
      if (this.slideCount <= 1) return;
      const normalizedIndex = ((index % this.slideCount) + this.slideCount) % this.slideCount;
      this.currentIndex = normalizedIndex;

      const renderIndex = this.getRenderIndexForLogicalIndex(normalizedIndex);
      const targetSlide = this.renderSlides[renderIndex];
      if (!targetSlide) return;

      this.slider.scrollTo({
        left: targetSlide.offsetLeft,
        behavior
      });
    }

    goToNext() {
      if (this.slideCount <= 1) return;
      const renderIndex = this.getClosestRenderIndex();
      const nextRenderIndex = Math.min(renderIndex + 1, this.renderSlides.length - 1);
      const targetSlide = this.renderSlides[nextRenderIndex];
      if (!targetSlide) return;

      this.slider.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth'
      });
    }

    goToPrevious() {
      if (this.slideCount <= 1) return;
      const renderIndex = this.getClosestRenderIndex();
      const prevRenderIndex = Math.max(renderIndex - 1, 0);
      const targetSlide = this.renderSlides[prevRenderIndex];
      if (!targetSlide) return;

      this.slider.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth'
      });
    }

    scheduleNormalize() {
      clearTimeout(this.normalizeTimer);
      this.normalizeRetryCount = 0;
      this.normalizeTimer = setTimeout(() => {
        this.tryNormalizeLoopPosition();
      }, NORMALIZE_DELAY);
    }

    tryNormalizeLoopPosition() {
      if (this.slideCount <= 1) return;
      const idleDuration = Date.now() - this.lastScrollEventAt;

      if (idleDuration < NORMALIZE_IDLE_MS) {
        this.normalizeRetryCount += 1;
        if (this.normalizeRetryCount > NORMALIZE_MAX_RETRIES) {
          this.normalizeLoopPosition();
          return;
        }
        this.normalizeTimer = setTimeout(() => {
          this.tryNormalizeLoopPosition();
        }, NORMALIZE_RETRY_DELAY);
        return;
      }

      this.normalizeRetryCount = 0;
      this.normalizeLoopPosition();
    }

    applyInstantScrollLeft(targetLeft) {
      const previousBehavior = this.slider.style.scrollBehavior;
      const previousSnapType = this.slider.style.scrollSnapType;
      let restored = false;

      const restoreStyles = () => {
        if (restored) return;
        restored = true;

        if (previousBehavior) {
          this.slider.style.scrollBehavior = previousBehavior;
        } else {
          this.slider.style.removeProperty('scroll-behavior');
        }

        if (previousSnapType) {
          this.slider.style.scrollSnapType = previousSnapType;
        } else {
          this.slider.style.removeProperty('scroll-snap-type');
        }
      };

      this.slider.style.scrollBehavior = 'auto';
      this.slider.style.scrollSnapType = 'none';

      try {
        this.slider.scrollLeft = targetLeft;
      } finally {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            restoreStyles();
          });
        });
      }
    }

    normalizeToLogicalIndex(index) {
      const normalizedIndex = ((index % this.slideCount) + this.slideCount) % this.slideCount;
      this.currentIndex = normalizedIndex;
      const renderIndex = this.getRenderIndexForLogicalIndex(normalizedIndex);
      const targetSlide = this.renderSlides[renderIndex];
      if (!targetSlide) return;
      this.applyInstantScrollLeft(targetSlide.offsetLeft);
    }

    normalizeLoopPosition() {
      if (this.slideCount <= 1) return;

      const renderIndex = this.getClosestRenderIndex();
      const trailingCloneStart = this.leadingCloneCount + this.slideCount;

      if (renderIndex < this.leadingCloneCount) {
        this.normalizeToLogicalIndex(this.slideCount - 1);
        return;
      }

      if (renderIndex >= trailingCloneStart) {
        this.normalizeToLogicalIndex(0);
      }
    }

    pauseAfterManualInteraction() {
      this.manualPausedUntil = Date.now() + MANUAL_RESUME_DELAY;
      this.stopAutoplay();

      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.startAutoplayIfEligible();
      }, MANUAL_RESUME_DELAY);
    }

    canAutoplay() {
      if (this.slideCount <= 1) return false;
      if (this.reducedMotionQuery.matches) return false;
      if (this.isDocumentHidden) return false;
      if (this.isHovered) return false;
      if (Date.now() < this.manualPausedUntil) return false;
      return true;
    }

    startAutoplayIfEligible() {
      if (!this.canAutoplay()) return;
      if (this.autoplay) return;

      this.autoplay = setInterval(() => {
        if (!this.canAutoplay()) {
          this.stopAutoplay();
          return;
        }
        this.goToNext();
      }, AUTOPLAY_INTERVAL);
    }

    stopAutoplay() {
      if (this.autoplay) {
        clearInterval(this.autoplay);
        this.autoplay = null;
      }
    }

    destroy() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
      clearTimeout(this.normalizeTimer);
      this.normalizeTimer = null;

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.abortController.abort();
      this.removeClones();
      instances.delete(this.root);
      liveInstances.delete(this);
    }
  }

  function initCartRecoSliders(container = document) {
    // 再描画で DOM から切り離された旧slider（autoplayタイマー/documentリスナー保持）を
    // 破棄してから init（Ajax再描画のたびに detached instance が残るリークを防ぐ・Codex P2）。
    Array.from(liveInstances).forEach((inst) => {
      if (inst.root && !inst.root.isConnected) inst.destroy();
    });
    container.querySelectorAll(SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new CartRecoSlider(root);
      // 部分構築のinstanceを登録しない（slider/prevButton/nextButtonが揃わず
      // abortControllerが作られなかった場合、destroy()のabort()がTypeErrorになるため・defer #20260901-EC-02）
      if (!instance.abortController) return;
      instances.set(root, instance);
      liveInstances.add(instance);
    });
  }

  function destroyCartRecoSliders(container) {
    if (!container || !container.querySelectorAll) return;

    container.querySelectorAll(SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initCartRecoSliders();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initCartRecoSliders(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyCartRecoSliders(event.target);
  });
})();
